import { AgNumberValueCellEditingRenderer } from 'components/AgGrid/AgNumberValueCellEditingRenderer';
import { WorkOrderPriceTypeEnum } from 'gql/graphql';

import type { ICellRendererParams } from 'ag-grid-community';
import { useCallback, useEffect, useMemo } from 'react';
import type { UseFormClearErrors, UseFormSetError } from 'react-hook-form/dist/types/form';

import type { IMoveVolumesItem } from '../hooks/useUerWorkOrderChangeUerRemainders';

interface IVolumeCellRendererComponent extends ICellRendererParams {
    onChange?: (id: string, value?: number | null) => void;
    idsToDuplicate?: number[];
    setError: UseFormSetError<any>;
    clearErrors: UseFormClearErrors<any>;
    volumesWithErrors: string[];
    errorMessage: string;
    decimalScale: number;
    duplicatesAreNotEditable?: boolean;
    isChangeDuplicate?: boolean;
    isDisabledGetter?: (id: number) => boolean;
    canRunDuplication?: (data: any) => boolean;
    checkedIds?: number[];
}

export const VolumeCellRendererComponent = (props: IVolumeCellRendererComponent) => {
    const {
        errorMessage,
        decimalScale,
        onChange,
        volumesWithErrors,
        setError,
        clearErrors,
        idsToDuplicate,
        data,
        api,
        duplicatesAreNotEditable,
        isDisabledGetter,
        canRunDuplication,
        checkedIds,
        isChangeDuplicate = true,
        value,
        ...rest
    } = props;

    const { remainder, id, type } = data as IMoveVolumesItem;

    const formCellId = useMemo(() => `volume_${id}`, [id]);
    const hasTooltip = useMemo(() => volumesWithErrors.includes(formCellId), [formCellId, volumesWithErrors]);

    const setValue = useCallback(
        (curValue?: number | null) => {
            const currRowNode = api.getRowNode(String(id));

            currRowNode?.setDataValue('volume', curValue);
            onChange?.(formCellId, curValue);

            if (canRunDuplication ? canRunDuplication(data) : type === WorkOrderPriceTypeEnum.Uer) {
                idsToDuplicate?.forEach((dId) => {
                    const rowNode = api.getRowNode(String(dId));

                    if (rowNode) {
                        if (isChangeDuplicate) {
                            onChange?.(`volume_${dId}`, curValue);
                        }
                        rowNode.setDataValue('volume', curValue);
                    }
                });
            }
        },
        [api, canRunDuplication, data, formCellId, id, idsToDuplicate, isChangeDuplicate, onChange, type],
    );

    const onChangeLocal = useCallback(
        (curValue: number | null) => {
            const isError = (curValue || 0) > remainder;

            if (isError) {
                setError(formCellId, { message: errorMessage });
            } else {
                clearErrors(formCellId);
            }

            setValue(curValue);
        },
        [remainder, setValue, setError, formCellId, errorMessage, clearErrors],
    );

    useEffect(() => {
        if (checkedIds && !checkedIds.includes(id)) {
            const currRowNode = api.getRowNode(String(id));

            currRowNode?.setDataValue('volume', undefined);
        }
    }, [api, checkedIds, id, setValue]);

    return (
        <AgNumberValueCellEditingRenderer
            data={data}
            decimalScale={decimalScale}
            idKey="id"
            columnKey="volume"
            onChange={onChangeLocal}
            status={hasTooltip ? 'alert' : undefined}
            tooltipStatus={hasTooltip ? 'alert' : undefined}
            isEditable={duplicatesAreNotEditable ? !idsToDuplicate?.includes(id) : true}
            tooltipDirection="rightCenter"
            tooltipMessage={hasTooltip ? errorMessage : undefined}
            api={api}
            disabled={isDisabledGetter?.(id)}
            value={value}
            {...rest}
        />
    );
};

VolumeCellRendererComponent.displayName = 'VolumeCellRendererComponent';
